import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Loader2, Search, Users, CheckCircle2, AlertTriangle } from 'lucide-react';

// Bulk "assign the same driver to N pending reviews" dialog (People > Driver Corrections).
export const BulkDriverAssignDialog = ({ api, candidateIds, open, onOpenChange, onResolved }) => {
  const [search, setSearch] = useState('');
  const [people, setPeople] = useState([]);
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const firstLoadRef = useRef(true);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelectedPerson(null);
    setReason('');
    setResult(null);
    // Start in loading state so the "No eligible people found" empty state
    // never flashes before the first fetch resolves (anti-flicker).
    setLoadingPeople(true);
    firstLoadRef.current = true;
  }, [open]);

  useEffect(() => {
    if (!open || !api) return undefined;
    let alive = true;
    // Fetch immediately on open; debounce only while the user is typing.
    const delay = firstLoadRef.current ? 0 : 300;
    firstLoadRef.current = false;
    const timer = setTimeout(async () => {
      setLoadingPeople(true);
      try {
        const res = await api.get(`/fleet/driver-people-directory?search=${encodeURIComponent(search.trim())}&limit=50`);
        if (alive) setPeople(res.data?.people || []);
      } catch {
        if (alive) setPeople([]);
      } finally {
        if (alive) setLoadingPeople(false);
      }
    }, delay);
    return () => { alive = false; clearTimeout(timer); };
  }, [open, api, search]);

  const submit = async () => {
    if (!selectedPerson || !reason.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post('/fleet/driver-candidates/bulk-resolve', {
        candidate_ids: candidateIds,
        person_id: selectedPerson.person_id,
        person_type: selectedPerson.person_type,
        reason: reason.trim(),
      });
      const resolved = res.data?.resolved || [];
      const skipped = res.data?.skipped || [];
      onResolved?.();
      if (skipped.length === 0) {
        toast.success(`${selectedPerson.person_name} assigned to ${resolved.length} trip${resolved.length === 1 ? '' : 's'}`);
        onOpenChange(false);
      } else {
        toast.warning(`${resolved.length} assigned, ${skipped.length} skipped`);
        setResult({ resolved, skipped });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk driver assignment failed');
    } finally {
      setSubmitting(false);
    }
  };

  const tripCount = candidateIds?.length || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto" data-testid="bulk-assign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="bulk-assign-title">
            <Users className="w-5 h-5" /> Bulk Assign Driver
          </DialogTitle>
          <DialogDescription data-testid="bulk-assign-description">
            Assign the same driver to <Badge variant="outline" className="px-1.5">{tripCount}</Badge> selected pending review{tripCount === 1 ? '' : 's'}.
            Declined ballot responses are overridden with your reason and fully audited.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3 py-1" data-testid="bulk-assign-result">
            <div className="flex gap-2 rounded-md border border-emerald-300 bg-emerald-50 p-2.5 text-xs text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <p><strong>{result.resolved.length}</strong> trip{result.resolved.length === 1 ? '' : 's'} assigned successfully.</p>
            </div>
            <div className="space-y-2" data-testid="bulk-assign-result-skipped">
              <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p><strong>{result.skipped.length}</strong> trip{result.skipped.length === 1 ? '' : 's'} could not be assigned:</p>
              </div>
              <ul className="space-y-1 text-xs max-h-40 overflow-y-auto">
                {result.skipped.map((row, idx) => (
                  <li key={row.candidate_id || idx} className="rounded border px-2 py-1.5" data-testid={`bulk-assign-skipped-${idx}`}>
                    <span className="font-medium">{row.vehicle_name || row.candidate_id}</span>
                    <span className="text-muted-foreground"> — {row.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex justify-end">
              <Button size="sm" onClick={() => onOpenChange(false)} data-testid="bulk-assign-result-close">Close</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="bulk-assign-search">Driver</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input
                  id="bulk-assign-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search drivers and eligible staff…"
                  className="pl-8"
                  data-testid="bulk-assign-search"
                />
              </div>
            </div>
            {/* Fixed height so the dialog never resizes/jumps between loading, empty and list states. */}
            <div className="h-56 overflow-y-auto rounded-md border divide-y" data-testid="bulk-assign-people-list">
              {loadingPeople && people.length === 0 ? (
                <div className="flex h-full items-center justify-center" data-testid="bulk-assign-people-loading">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
              ) : people.length === 0 ? (
                <p className="text-xs text-muted-foreground p-3" data-testid="bulk-assign-people-empty">No eligible people found.</p>
              ) : (
                people.map((p) => {
                  const selected = selectedPerson?.person_id === p.person_id;
                  return (
                    <button
                      key={p.person_id}
                      type="button"
                      onClick={() => setSelectedPerson(p)}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-muted/60 ${selected ? 'bg-primary/10' : ''}`}
                      data-testid={`bulk-assign-person-${p.person_id}`}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="font-medium truncate">{p.person_name}</span>
                        <Badge variant="outline" className="text-[9px] px-1 py-0 capitalize shrink-0">{p.person_type}</Badge>
                      </span>
                      {selected && <CheckCircle2 className="w-4 h-4 text-primary shrink-0" data-testid={`bulk-assign-person-selected-${p.person_id}`} />}
                    </button>
                  );
                })
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bulk-assign-reason">Reason (required)</Label>
              <Textarea
                id="bulk-assign-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                placeholder="Why is this driver being assigned to these trips?"
                data-testid="bulk-assign-reason"
              />
              <p className="text-[11px] text-muted-foreground">This reason is recorded in the audit log for every trip in the batch.</p>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="bulk-assign-cancel">Cancel</Button>
              <Button
                size="sm"
                className="bg-[#2563eb] hover:bg-[#1d4ed8]"
                onClick={submit}
                disabled={submitting || !selectedPerson || !reason.trim()}
                data-testid="bulk-assign-confirm"
              >
                {submitting
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : `Assign to ${tripCount} trip${tripCount === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BulkDriverAssignDialog;
